/**
 * Pure filter logic for the Analysis tables. No React, no DOM beyond
 * `localStorage` access, so every rule here is directly unit-testable.
 *
 * Semantics, consistent across every table: OR within a group, AND across
 * groups. An empty set means "no constraint" — never "match nothing".
 */

/**
 * Sentinel for the Owner filter's "Unassigned" pseudo-option.
 *
 * Consumers MUST coalesce in the accessor — `accessor: (item) => item.ownerId ??
 * UNASSIGNED_OWNER`. The plain `accessor: (item) => item.ownerId` rejects null
 * (see `matchesGroup`), so the "Unassigned" checkbox would match zero rows.
 */
export const UNASSIGNED_OWNER = '__unassigned__'

export interface FilterOptionDef {
  value: string
  label: string
}

export interface FilterGroupConfig<T> {
  /** Trigger label, e.g. "Severity". Also used in chip text. */
  label: string
  options: FilterOptionDef[]
  /** The item's value for this group. Ignored when `matcher` is set. */
  accessor?: (item: T) => string | null | undefined
  /** Custom predicate for groups whose match is not a plain equality. */
  matcher?: (item: T, value: string) => boolean
  /** Hide the dropdown entirely when fewer than two options exist. */
  hideWhenSparse?: boolean
  /**
   * Skip stale-value pruning. Set on data-derived groups (Owner) whose
   * options arrive asynchronously — without it, a restored selection is
   * discarded on the first render, before team members have loaded.
   */
  skipPrune?: boolean
  /**
   * Chip text for a selected value that no option can name.
   *
   * Only reachable on a `skipPrune` group: everywhere else the options come
   * from a fixed enum, so every selectable value has a label. Owner options are
   * derived from the loaded rows plus the team members, and the selection
   * outlives both — the member list may not have arrived, the fetch may have
   * failed (it is swallowed), or a re-run may have streamed rows that carry no
   * owner. Without this the chip printed the stored value itself, which is a
   * bare UUID sitting over an empty table.
   */
  missingLabel?: string
}

export type FilterGroups<T> = Record<string, FilterGroupConfig<T>>
export type FilterValues = Record<string, Set<string>>

export function matchesGroup<T>(
  item: T,
  config: FilterGroupConfig<T>,
  selected: Set<string> | undefined
): boolean {
  if (!selected || selected.size === 0) return true
  if (config.matcher) {
    for (const value of selected) {
      if (config.matcher(item, value)) return true
    }
    return false
  }
  const actual = config.accessor?.(item)
  return actual != null && selected.has(actual)
}

export function matchesFilters<T>(
  item: T,
  groups: FilterGroups<T>,
  values: FilterValues
): boolean {
  for (const key of Object.keys(groups)) {
    const config = groups[key]
    if (config && !matchesGroup(item, config, values[key])) return false
  }
  return true
}

/**
 * Owner options: only members who actually own a row, plus "Unassigned" when
 * some row has none. Keeps the dropdown to people relevant to this bid.
 */
export function buildOwnerOptions<T extends { ownerId: string | null }>(
  items: T[],
  teamMembers: Array<{ userId: string; fullName: string | null }>
): FilterOptionDef[] {
  const present = new Set(items.map((item) => item.ownerId ?? UNASSIGNED_OWNER))
  const named = teamMembers
    .filter((member) => present.has(member.userId))
    .map((member) => ({ value: member.userId, label: member.fullName ?? 'Unknown user' }))
    .sort((a, b) => a.label.localeCompare(b.label))
  return present.has(UNASSIGNED_OWNER)
    ? [...named, { value: UNASSIGNED_OWNER, label: 'Unassigned' }]
    : named
}

/**
 * Parse one URL filter param. Accepts a bare value or a comma list, so the
 * single-value deep links that predate multi-select keep working unchanged.
 */
export function parseFilterList(raw: string | null, allowed: Set<string>): Set<string> {
  if (!raw) return new Set()
  return new Set(
    raw
      .split(',')
      .map((part) => part.trim())
      .filter((part) => allowed.has(part))
  )
}

/**
 * Drop values (and whole groups) that the current config no longer knows
 * about. A filter left over from before an enum change must never silently
 * hide rows.
 */
export function pruneValues<T>(values: FilterValues, groups: FilterGroups<T>): FilterValues {
  const out: FilterValues = {}
  for (const [key, selected] of Object.entries(values)) {
    const config = groups[key]
    if (!config) continue
    const kept = config.skipPrune
      ? new Set(selected)
      : new Set([...selected].filter((v) => config.options.some((o) => o.value === v)))
    if (kept.size > 0) out[key] = kept
  }
  return out
}

const FILTER_KEY_PREFIX = 'bidfilters'

/**
 * Scoped by USER as well as bid and table.
 *
 * Filters decide which rows a completeness check shows, so inheriting them from
 * whoever used the browser last is a trust problem rather than a preference
 * one: sign in on a shared machine and the compliance matrix opens already
 * narrowed by someone else, with only the chip rail to say so. Nothing expired
 * or cleaned up either, so that state persisted indefinitely.
 *
 * A signed-out reader gets their own bucket rather than sharing the first
 * account's.
 */
export function filterStorageKey(
  userId: string | null | undefined,
  bidId: string,
  tableKey: string
): string {
  return `${FILTER_KEY_PREFIX}:${userId ?? 'anon'}:${bidId}:${tableKey}`
}

/**
 * Drop the pre-user-scoping keys, which nothing can read any more.
 *
 * Told apart by segment count — `bidfilters:<bid>:<table>` is three, the
 * current `bidfilters:<user>:<bid>:<table>` is four — so a live key is never
 * mistaken for a dead one, and keys belonging to OTHER users are deliberately
 * left alone: separating them is the point.
 */
export function purgeLegacyFilterKeys(): void {
  try {
    const doomed: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key?.startsWith(`${FILTER_KEY_PREFIX}:`) && key.split(':').length === 3) {
        doomed.push(key)
      }
    }
    for (const key of doomed) window.localStorage.removeItem(key)
  } catch {
    // Storage unavailable (private mode, quota). Nothing here is load-bearing.
  }
}

export function readStoredFilters<T>(key: string, groups: FilterGroups<T>): FilterValues {
  let parsed: unknown
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return {}
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
  const values: FilterValues = {}
  for (const [groupKey, list] of Object.entries(parsed as Record<string, unknown>)) {
    if (!Array.isArray(list) || list.some((v) => typeof v !== 'string')) return {}
    values[groupKey] = new Set(list as string[])
  }
  return pruneValues(values, groups)
}

export function writeStoredFilters(key: string, values: FilterValues): void {
  try {
    const payload: Record<string, string[]> = {}
    for (const [groupKey, selected] of Object.entries(values)) {
      if (selected.size > 0) payload[groupKey] = [...selected]
    }
    if (Object.keys(payload).length === 0) {
      window.localStorage.removeItem(key)
      return
    }
    window.localStorage.setItem(key, JSON.stringify(payload))
  } catch {
    /* private mode / quota — degrade to in-memory state, never throw */
  }
}
